import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { MatMenu } from '@angular/material/menu';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import jwt_decode from "jwt-decode";
import moment from 'moment';
import { EventbusConnectionService, NotificationAlert } from 'app/services/eventbus-connection.service';
import { DashboardSidebarService } from '../dashboard-sidebar/services/dashboard-sidebar.service';
import { IFeatures, IPerspectiveDashboard } from 'app/services/graphqlApi.service';
import { PanelService } from 'app/shared/panels/chart/Services/panel.service';
import { DynamicDrawerService } from 'app/shared/dynamic-drawer.service';
import { CommonService } from 'app/shared/services';
import { IDashboardResponse, IPreferences, IRangeForm } from 'app/shared/panels/chart/Models/chart-settings.model';
import { EEntityTypes, ERanges, ERefreshAlarm, ERefreshRate, EViewTypes } from 'app/shared/enums';
import { CountryLanguages, ICountryCode } from 'app/utils/countries';
import { timezones, ITimezone } from 'app/utils/timezones';
import { Perspective } from 'app/perspectives/interfaces/interfaces';
import { IDropdown } from 'app/shared/interfaces/model';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})

export class AppHeaderComponent implements OnInit, OnDestroy {
  @Input() set exitFullScreen(status: boolean) {
    if (status) {
      this.showUpperHeader = false;
    }
  }
  @Input() isAdminView: boolean = false;
  @Input() allFeaturesNames: string[] = [];
  @Output() onAlarmsToggle = new EventEmitter();
  @Output() upperHeader = new EventEmitter();
  @Output() selectedDashboard = new EventEmitter();
  @ViewChild('tsDataMenu') tsDataMenu: MatMenu;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'F1') { // to switch screen
      this.isAdminSide.setValue(!this.isAdminSide.value);
      event.preventDefault();
    } else if (event.key === 'F2') { //  to switch theme
      this.isLightMode.setValue(!this.isLightMode.value);
      this.onLightModeChange(this.isLightMode.value);
      event.preventDefault();
    }
  }
  alarmField = new FormControl(false);
  readonly destroyed = new ReplaySubject(1);
  isAdminSide = new FormControl(false);
  isLightMode = new FormControl(false);
  selectedTimezoneName = new FormControl('');
  intervalField = new FormControl('');
  notifications: NotificationAlert[] = [];
  featuresDetails: IFeatures[] = [];
  allPerspectives: Perspective[] = [];
  countriesLanguages: ICountryCode[] = CountryLanguages;
  selectedCountryLang: ICountryCode = CountryLanguages[0]; // 'US English' is default selected language
  isAllDashboardCheck: boolean = false;
  isAllAdminCheck: boolean = false;
  showNotification = false;
  isSuperAdmin: boolean = false;
  isShowTimeSetting: boolean = false;
  showUpperHeader: boolean = true;
  showDashboardsOnly: boolean = false;
  isTSCallExists: boolean = false;
  isLiveDataCallExists: boolean = true;
  viewTypes = EViewTypes;
  userInfo: { userName: string; email: string } = { userName: '', email: '' };
  refreshTime: number = null;
  refreshTypes = ERefreshRate;
  selectedInterval: string = ERefreshRate.S30;
  selectedPerspective: Perspective = null;
  dashboardsWithChildren: IDashboardResponse[] = [];
  allDashboards: IDashboardResponse[] = [];
  timezones: ITimezone[] = timezones;
  filteredTimeZones: Observable<ITimezone[]> = of([]);
  selectedTimeZoneValue: string = 'Local browser time';
  selectedDashboardName: string = '';
  refreshIntervals: IDropdown[] = [
    { name: ERefreshRate.Off, value: ERefreshRate.Off },
    { name: ERefreshRate.S10, value: ERefreshRate.S10 },
    { name: ERefreshRate.S30, value: ERefreshRate.S30 },
    { name: ERefreshRate.M1, value: ERefreshRate.M1 },
    { name: ERefreshRate.M5, value: ERefreshRate.M5 },
    { name: ERefreshRate.M10, value: ERefreshRate.M10 },
    { name: ERefreshRate.M15, value: ERefreshRate.M15 },
  ];
  startDate: any;
  endDate: any;
  toDate: string = '';
  fromDate: string = '';
  touchUi: boolean = false;
  ranges: IDropdown[] = [{ name: 'Last 1 hour', value: ERanges.H1 }, { name: 'Last 6 hours', value: ERanges.H6 }, { name: 'Last 12 hours', value: ERanges.H12 }, { name: 'Last 1 day', value: ERanges.D1 }, { name: 'Last 1 week', value: ERanges.W1 }, { name: 'Last 1 month', value: ERanges.M1 }, { name: 'Last 3 months', value: ERanges.M3 }];
  // dateTimePicker
  date: Date;
  isRangeApplied: boolean = false;
  enableMeridian: boolean = false;
  showSpinners: boolean = true;
  showSeconds: boolean = false;
  selectedRangeName: string = '';
  minDateFrom: Date;
  maxDateFrom: Date;
  minDateTo: Date;
  maxDateTo: Date;
  stepHour = 1;
  stepMinute = 1;
  stepSecond = 1;
  rangeForm: FormGroup = this.fb.group({
    range: '',
    fromDate: ['', Validators.required],
    toDate: ['', Validators.required]
  });

  constructor(
    private readonly router: Router,
    private readonly commonService: CommonService,
    private readonly panelService: PanelService,
    private readonly dashboardSidebarService: DashboardSidebarService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    public readonly drawerService: DynamicDrawerService<any>,
    private readonly eventbusConnectionService: EventbusConnectionService) {
    const isAdmin: boolean = JSON.parse(localStorage.getItem('isAdmin')) || false;
    this.isAdminSide.setValue(isAdmin ? true : false);
    this.maxDateFrom = new Date(Date.now());
    this.maxDateTo = new Date(Date.now());
    this.panelService._lineChartsHeaderRanges.pipe(
      takeUntil(this.destroyed)).subscribe((lineChartHeader: IRangeForm) => {
        if (!lineChartHeader) {
          console.log('lineChartHeader: ', lineChartHeader);
          this.rangeForm.reset();
          this.onRangeChange(ERanges.D1, this.ranges[3].name);
          this.isRangeApplied = true;
        }
      });
  }

  ngOnInit(): void {
    const perferences: IPreferences = JSON.parse(localStorage.getItem('preferences'));
    this.commonService._isLightMode.next(this.isLightMode.value);
    this.themeApply();
    // set language and timezone preferences
    this.setPreferences(perferences);
    this.intervalField.setValue(ERefreshRate.S30);
    this.onLiveIntervalChange(ERefreshRate.S30);
    const storedToken = JSON.parse(localStorage.getItem('Token'));
    this.userInfo.userName = JSON.parse(localStorage.getItem('Token'))?.userName || '';
    const decodedToken: any = jwt_decode(storedToken.accessToken);
    this.userInfo.email = decodedToken?.email || '';
    this.isSuperAdmin = (this.userInfo.userName === "superadmin") ? true : false;
    this.featuresDetails = JSON.parse(localStorage.getItem("Features")) || [];
    if (this.featuresDetails.length) {
      this.isAllDashboardCheck = this.featuresDetails.every((feature: IFeatures) => feature.is_config === false);
      this.isAllAdminCheck = this.featuresDetails.every((feature: IFeatures) => feature.is_config === true);
    }

    this.eventbusConnectionService.eventBroadcastResponse$.pipe(
      takeUntil(this.destroyed),
      filter(n => n?.type === 'notification'))
      .subscribe(notification => {
        console.log('socket notification:1 ', notification);
        console.log('this.isAdminView:1 ', this.isAdminView);
        if (notification.data?.content?.includes('href=')) { // auto create asset WS hyperlink handling
          if (this.isAdminSide) {
            this.notifications.unshift(notification.data);
          }
        } else {
          this.notifications.unshift(notification.data);
        }
      });

    this.eventbusConnectionService.eventSelectedUserResponse$.pipe(
      takeUntil(this.destroyed),
      filter(n => n?.type === 'notification'))
      .subscribe(notification => {
        console.log('socket notification:2 ', notification);
        console.log('this.isAdminView:2 ', this.isAdminView);
        this.notifications.unshift(notification.data);
      });

    this.isAdminSide.valueChanges.pipe(
      takeUntil(this.destroyed)).subscribe((status: boolean) => {
        if (status) {
          this.router.navigate(["/components"]);
          localStorage.setItem('isAdmin', JSON.stringify(true));
          this.alarmField.setValue(false);
          this.panelService._viewToggle.next(EViewTypes.ADMIN);
        } else {
          this.router.navigate(["/dashboard"]);
          localStorage.setItem('isAdmin', JSON.stringify(false));
          this.panelService._viewToggle.next(EViewTypes.DASHBOARD);
        }
      });

    if (this.isAlarmsPage) {
      this.alarmField.setValue(this.isAlarmsPage);
    }

    // load perspectives list
    this.loadPerspectives();
    // handle left menu toggle states
    this.leftMenuToggle(true);
    // all dashboads for selected node (group or asset)
    this.dashboardSidebarService.allDashboards$.pipe(takeUntil(this.destroyed)).subscribe(dashboards => {
      this.selectedDashboardName = '';
      this.dashboardsWithChildren = [];
      this.allDashboards = [];
      const dashboardsWithChildren: IDashboardResponse[] = [];
      this.allDashboards = dashboards;
      console.log('dashboards: ', dashboards);
      if (this.allDashboards?.length) {
        this.allDashboards.forEach(dashboard => {
          if (dashboard.path !== '') {
            dashboardsWithChildren.push(dashboard);
          }

          if (dashboard.is_default) {
            this.selectedDashboardName = dashboard.name;
          }
        });

        // remove duplicates
        this.dashboardsWithChildren = dashboardsWithChildren?.filter((value, index, self) => index === self.findIndex((t) => t.path === value.path));
        this.dashboardsWithChildren.forEach(c => {
          c.children = [];
        });

        this.dashboardsWithChildren?.forEach((c: IDashboardResponse) => {
          this.allDashboards.map(d => {
            if (c.path === d.path) {
              c.children.push(d);
            }
          })
        })
      } else {
        this.selectedDashboardName = '';
      }
      // console.log('this.allDashboards: full', this.allDashboards);
      this.selectedDashboard.emit(this.selectedDashboardName);
    });

    // selected group dashboards
    this.dashboardSidebarService.selectedGroupDashboards$.pipe(takeUntil(this.destroyed)).subscribe((groupDashboards: IPerspectiveDashboard[]) => {
      console.log('selectedGroupDashboards: ', groupDashboards);
      if (!groupDashboards.length) {
        this.allDashboards = [];
        this.dashboardsWithChildren = [];
        this.isTSCallExists = false;
        this.isLiveDataCallExists = false;
      }
    });

    // check if TSCall exists against selected dashboard
    this.panelService.isTSCallExists$.pipe(takeUntil(this.destroyed)).subscribe((isTSCallExists: boolean) => {
      this.isTSCallExists = isTSCallExists;
    });

    // check if LiveDataCall exists against selected dashboard
    this.panelService.isLiveDataCallExists$.pipe(takeUntil(this.destroyed)).subscribe((isLiveDataCallExists: boolean) => {
      this.isLiveDataCallExists = isLiveDataCallExists;
    });

    // trigger auto complete on timezome
    this.autoCompleteTimeZone();
  }

  get isDashboardPage() {
    return this.router.url.includes('/dashboard');
  }

  get isAlarmsPage() {
    return this.router.url.includes(EEntityTypes.USER_ALARMS);
  }

  get checkForwardValidation() {
    if (!this.startDate || !this.endDate) {
      return true;
    } else {
      var endDate = new Date(new Date(this.rangeForm.value.toDate)).getTime();
      var currentTime = new Date(new Date(Date.now())).getTime();
      if (endDate > currentTime) {
        return true;
      } else {
        return false;
      }
    }
  }

  //#region onLogoClick, backToDashboard, onLiveIntervalChange and onRefreshDashboard methods

  /**
   * on logo click
   */
  onLogoClick() { window.location.reload() }

  /**
   * back to dashboard page from alarms page
   */
  backToDashboard() {
    this.router.navigate(['/dashboard']);
    this.panelService._isBackToDashbaord.next(true);
    this.selectedInterval = ERefreshRate.S30;
    this.upperHeader.emit(this.showUpperHeader);
    if (this.isAlarmsPage) {
      this.onLiveIntervalChange(ERefreshRate.S30);
      this.panelService._dashboardInterval.next(this.refreshTime);
      this.themeApply();
    }
  }

  /**
 * on live calls interval change
 * @param interval interval
 */
  onLiveIntervalChange(interval: string) {
    this.selectedInterval = interval;
    switch (interval) {
      case this.refreshTypes.Off:
        this.refreshTime = null;
        this.intervalField.reset();
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.S10:
        this.refreshTime = 10000; // ms
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.S30:
        this.refreshTime = 30000;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.M1:
        this.refreshTime = 60000;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.M5:
        this.refreshTime = 300000;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.M10:
        this.refreshTime = 600000;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
      case this.refreshTypes.M15:
        this.refreshTime = 900000;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;

      default:
        this.refreshTime = null;
        this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
        break;
    }
  }

  /**
 * on refresh dashbaord
 */
  onRefreshDashboard() {
    this.isDashboardPage ? this.panelService._dashboardInterval.next(this.refreshTime) : this.panelService._alarmsInterval.next(this.refreshTime);
  }

  //#endregion

  //#region logout, onCountrySelection, loadPerspectives, perspectiveSelected, setDefaultPerspective and leftMenuToggle methods

  /**
   *
   */
  logOut() {
    localStorage.clear();
    this.router.navigate(['/auth/login']);
  }

  /**
   * on country selection
   * @param countryCode contrycode
   */
  onCountrySelection(countryCode: string) {
    const selectedCountry: ICountryCode = this.countriesLanguages.find((country: ICountryCode) => country.code === countryCode)
    this.selectedCountryLang = selectedCountry;
    const preferences: IPreferences = JSON.parse(localStorage.getItem('preferences'));
    preferences.language = this.selectedCountryLang.code;
    localStorage.setItem('preferences', JSON.stringify(preferences));
    this.commonService._preferences.next(preferences);
    this.translate.use(this.selectedCountryLang.code);
  }

  /**
   * load perspectives
   */
  loadPerspectives() {
    this.dashboardSidebarService.allPerspectives$.pipe(takeUntil(this.destroyed)).subscribe((allPerspectives: Perspective[]) => {
      if (allPerspectives?.length) {
        this.allPerspectives = allPerspectives;
        console.log('this.allPerspectives: ', this.allPerspectives);
        this.selectedPerspective = this.allPerspectives.find(perspective => !!perspective.isDefaultPerspective);
      }
    });
  }

  /**
   * on perspective selection
   * @param name perspective name
   */
  perspectiveSelected(name: string) {
    console.log('perspectiveSelected: ');
    const selectedPerspective: Perspective = this.allPerspectives.find((perspective: Perspective) => perspective.name == name)
    if (selectedPerspective) {
      this.selectedPerspective = selectedPerspective;
      this.dashboardSidebarService.selectedPerspective.next(null); // to avoid double perspective's default dashboard calls on perspective change
      this.dashboardSidebarService.setPerspectiveById(selectedPerspective.id);
      this.dashboardSidebarService._selectedGroupDashboards.next([]);
    }
  }

  /**
 * set perspective as a default
 * @param selectedPerspectiveId selected perspective id
 * @param perspectiveMenuTrigger perspective menu trigger
 */
  setDefaultPerspective(selectedPerspectiveId: string) {
    const selectedPerspective: Perspective = this.allPerspectives.find(perspective => perspective.id === selectedPerspectiveId);
    if (!selectedPerspective.isDefaultPerspective) {
      this.dashboardSidebarService._setPerspectiveAsDefault.next(selectedPerspectiveId);
    }
  }

  /**
   * left menu toggle states
   */
  leftMenuToggle(isFirst: boolean = false) {
    const isAdmin: boolean = JSON.parse(localStorage.getItem('isAdmin'));
    this.panelService._leftSideMenuState.next(isAdmin ? isAdmin : isFirst ? null : false);
  }

  //#endregion

  //#region showTimeSettingMenu, onDashboardMenuItemClick, hideUpperHeader and fullScreenExit methods

  /**
   * show time setting menu
   */
  showTimeSettingMenu() {
    this.isShowTimeSetting = !this.isShowTimeSetting;
  }

  /**
   * ond dashboard menu item click
   * @param dashboardId dashbaord id
   */
  onDashboardMenuItemClick(dashboardId: string) {
    // reset line chart ranges
    this.panelService._selectedPanelRange.next(null);
    this.panelService._lineChartsHeaderRanges.next(null);
    const selectedDashboardIndex: number = this.allDashboards?.findIndex(d => d.id === dashboardId);
    this.selectedDashboardName = this.allDashboards[selectedDashboardIndex].name;
    this.selectedDashboard.emit(this.selectedDashboardName);

    if (dashboardId) {
      this.dashboardSidebarService._selectedDashboard.next(dashboardId);
    }
  }

  /**
   * hide upper header portion
   */
  hideUpperHeader() {
    this.showUpperHeader = !this.showUpperHeader;
    this.upperHeader.emit(this.showUpperHeader);
  }

  /**
   * exit full screen
   */
  fullScreenExit() {
    this.showDashboardsOnly = !this.showDashboardsOnly
    this.drawerService._showFullDashboard.next(true)
  }

  //#endregion

  //#region onToDateChange, onFromDateChange, onRangeChange, getStartDate, submitTSRange, customTSTooltip, openRangeForm, onTSSpanChange, and zoomoutLineCharts (TS methods)

  /**
   * on ToDate change
   * @param value value
   */
  onToDateChange(value) {
    this.selectedRangeName = '';
    console.log('value: ', value);
    if (value) {
      this.rangeForm.patchValue({ range: null });
      this.toDate = value;
      this.onFromDateChange(undefined, value);
    }
  }

  /**
   * on from date change
   * @param value value
   * @param toDate todate
   */
  onFromDateChange(value, toDate?: any) {
    this.selectedRangeName = '';
    if (value) {
      this.rangeForm.patchValue({ range: null });
      this.fromDate = value;
    }

    this.startDate = this.rangeForm.value?.fromDate ? new Date(this.rangeForm.value?.fromDate).toLocaleString().replace(/,/g, '') : '';
    this.endDate = this.rangeForm.value?.toDate ? new Date(this.rangeForm.value?.toDate).toLocaleString().replace(/,/g, '') : '';
    console.log('this.endDate:1 ', this.endDate);
    if (!this.startDate) {
      const fromDate = Date.parse(this.fromDate);
      this.startDate = new Date(fromDate);
    }

    if (!this.endDate) {
      const endDate = Date.parse(toDate);
      this.endDate = new Date(endDate);
      console.log('this.endDate:2 ', this.endDate);
    }
  }

  /**
   * on range change
   * @param selectedRange selected range
   * @param rangeName range name
   */
  onRangeChange(selectedRange: ERanges, rangeName: string) {
    this.selectedRangeName = rangeName;
    this.maxDateFrom = new Date(Date.now());
    this.maxDateTo = new Date(Date.now());
    this.getStartDate(selectedRange);
  }

  /**
   * get start date
   * @param range range
   */
  getStartDate(range: ERanges) {
    const currentDateTime: number = Date.now() - (1 * 60 * 1000); // - 1 min
    this.endDate = new Date(currentDateTime).toLocaleString().replace(/,/g, '');
    switch (range) {
      case ERanges.H1: {
        this.startDate = new Date(currentDateTime - 3600000).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - 3600000), toDate: new Date(currentDateTime), range: range });
        break;
      }
      case ERanges.H6: {
        this.startDate = new Date(currentDateTime - 21600000).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - 21600000), toDate: new Date(currentDateTime), range: range });
        break;
      }
      case ERanges.H12: {
        this.startDate = new Date(currentDateTime - 43200000).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - 43200000), toDate: new Date(currentDateTime), range: range });
        break;
      }
      case ERanges.D1: {
        this.startDate = new Date(currentDateTime - 86400000).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - 86400000), toDate: new Date(currentDateTime), range: range });
        break;
      }
      case ERanges.W1: {
        this.startDate = new Date(currentDateTime - (86400000 * 7)).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - (86400000 * 7)), toDate: new Date(currentDateTime), range: range });
        console.log('this.rangeForm.value: ', this.rangeForm.value);
        break;
      }
      case ERanges.M1: {
        this.startDate = new Date(currentDateTime - (86400000 * 30)).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - (86400000 * 30)), toDate: new Date(currentDateTime), range: range });
        break;
      }
      case ERanges.M3: {
        this.startDate = new Date(currentDateTime - (86400000 * 90)).toLocaleString().replace(/,/g, '');
        this.rangeForm.patchValue({ fromDate: new Date(currentDateTime - (86400000 * 90)), toDate: new Date(currentDateTime), range: range });
        break;
      }
      default: {
        console.log("!Invalid Range");
        break;
      }
    }

    console.log('this.rangeForm.value :>> ', this.rangeForm);
  }

  /**
   * on apply TS range button
   */
  submitTSRange() {
    const objRangeForm: IRangeForm = { range: this.rangeForm.value?.range ? this.rangeForm.value?.range : null, fromDate: this.rangeForm.value?.fromDate ? moment(this.rangeForm.value?.fromDate).toDate() : moment(this.startDate).toDate(), toDate: this.rangeForm.value?.toDate ? moment(this.rangeForm.value?.toDate).toDate() : moment(this.endDate).toDate() }
    console.log('objRangeForm: Submit', objRangeForm);
    this.panelService._lineChartsHeaderRanges.next(objRangeForm);
    this.isRangeApplied = true;
    this.tsDataMenu.closed.emit();
  }

  /**
   * selected time range Tooltip
   */
  customTSTooltip() {
    if (this.startDate && this.endDate && this.isRangeApplied) {
      const tooltip: string = " " + this.startDate + "\n to \n " + this.endDate + "\n" + this.selectedTimeZoneValue;
      return tooltip;
    } else { return '' }
  }

  /**
   * open range form
   */
  openRangeForm() {
    if (!this.isRangeApplied) {
      this.rangeForm.reset();
      this.isRangeApplied = false;
    }
  }

  /**
 * reset TS Range
 */
  zoomoutLineCharts() {
    this.panelService._isZoomout.next(true);
  }

  /**
 * on Time-series span change
 * @param type type ('forward' or 'backward')
 */
  onTSSpanChange(TSSpanType: string) {
    const rangeFormData: IRangeForm = this.panelService._lineChartsHeaderRanges.getValue();
    if (TSSpanType === 'backward') {
      var toDate: Date = new Date(new Date(rangeFormData.toDate).setDate(new Date(rangeFormData.toDate).getDate() - 1));
      var fromDate: Date = new Date(new Date(rangeFormData.fromDate).setDate(new Date(rangeFormData.fromDate).getDate() - 1));
      this.rangeForm.patchValue({ fromDate: new Date(fromDate), toDate: new Date(toDate) });
      this.startDate = toDate;
      this.endDate = fromDate;
    } else {
      var toDate: Date = new Date(new Date(rangeFormData.toDate).setDate(new Date(rangeFormData.toDate).getDate() + 1));
      var fromDate: Date = new Date(new Date(rangeFormData.fromDate).setDate(new Date(rangeFormData.fromDate).getDate() + 1));
      this.rangeForm.patchValue({ fromDate: new Date(fromDate), toDate: new Date(toDate) });
      this.startDate = toDate;
      this.endDate = fromDate;
    }

    this.submitTSRange();
  }

  //#endregion

  //#region alarmToggle and onRefreshAlarms methods

  /**
   * alarm toggle
   */
  alarmToggle() {
    this.selectedInterval = ERefreshRate.S30;
    if (this.isDashboardPage) {
      this.panelService._alarmsInterval.next(30000);
    } else if (this.isAlarmsPage) {
      this.refreshTime = 30000;
      this.panelService._dashboardInterval.next(this.refreshTime);
      this.themeApply();
    }
  }

  /**
   * on refresh alarm
   */
  onRefreshAlarms() {
    this.panelService._alarmsInterval.next(ERefreshAlarm.RefreshAlarm);
  }

  //#endregion

  //#region autoCompleteTimeZone, selectedZone and onTimezoneFocusOut methods

  /**
   * autocomplete time zone
   */
  autoCompleteTimeZone() {
    const timeZones$ = of(this.timezones);
    this.filteredTimeZones = combineLatest([timeZones$, this.selectedTimezoneName.valueChanges.pipe(startWith(''))]).pipe(map(([timeZones, value]) => {
      const lowercaseValue: string = value?.toLowerCase() || '';
      if (lowercaseValue) {
        return lowercaseValue ? timeZones?.filter((property: ITimezone) => ( property.name.toLowerCase().includes(lowercaseValue) || property.country.toLowerCase().includes(lowercaseValue) || property.offset.toLowerCase().includes(lowercaseValue)   ) ) : [...timeZones];
      } else {
        return [...timeZones];
      }
    }));

  }

  /**
   * on timezone selection
   * @param zoneName time zone name
   */
  onTimezoneSelection(zoneName: string) {
    const selectedTimezone: ITimezone = this.timezones.find((timeZone) => timeZone.name === zoneName);
    if (selectedTimezone) {
      selectedTimezone.timeValue = parseFloat(selectedTimezone.offset.replace(":", ".").slice(3, selectedTimezone.offset.length));
      this.selectedTimeZoneValue = selectedTimezone.offset;
      this.selectedTimezoneName.setValue(selectedTimezone.name +' '+ selectedTimezone.offset);
      localStorage.setItem('preferences', JSON.stringify({language: this.selectedCountryLang.code, timezone: { name: selectedTimezone.name, offset: selectedTimezone.timeValue }}));
      this.commonService._preferences.next({language: this.selectedCountryLang.code, timezone: { name: selectedTimezone.name, offset: selectedTimezone.timeValue }});
    }
  }

  /**
   * close TZ panel on time zone focus out
   */
  onTimezoneFocusOut() {
    setTimeout(() => { this.autocomplete.closePanel() }, 500);
  }

  //#endregion

  //#region onLightModeChange, setPreferences and themeApply methods

  /**
   * on light model change
   * @param isLightMode checked status
   */
  onLightModeChange(isLightMode: boolean) {
    this.commonService._isLightMode.next(isLightMode);
    this.themeApply();
  }

    /**
   * set language and timezone preferences
   * @param perferences language and timezone
   */
  setPreferences(perferences: IPreferences){
    if (perferences?.language) {
      const countryLanguage: ICountryCode = this.countriesLanguages.find((countryLanguage: ICountryCode) => countryLanguage.code === perferences?.language);
      this.selectedCountryLang = countryLanguage;
      this.translate.use(this.selectedCountryLang?.code);
    } else {
      this.translate.use(this.selectedCountryLang.code);
    }

    var selectedTimezone = { name: '', offset: null };
    if(perferences?.timezone){
      const timezone: ITimezone = this.timezones.find((tz: ITimezone) => tz.name === perferences.timezone.name);
      this.selectedTimeZoneValue = timezone.offset;
      this.selectedTimezoneName.setValue(timezone.name+' '+timezone.offset);
      selectedTimezone = { name: perferences.timezone.name, offset: perferences.timezone.offset };
    } else{
      const offset: number = new Date().getTimezoneOffset(), o = Math.abs(offset);
      const currentTimeZoneOffSet: string = (offset < 0 ? "+" : "-") + ("00" + Math.floor(o / 60)).slice(-2) + ":" + ("00" + (o % 60)).slice(-2);
      let defaultTimezone: ITimezone = this.timezones.find((timezone: ITimezone) => timezone.offset.includes(currentTimeZoneOffSet));
      if(!defaultTimezone){
         defaultTimezone = this.timezones.find(timezone => timezone.country === 'Asia/Karachi');
      }

      defaultTimezone.timeValue = parseFloat(defaultTimezone.offset.replace(":", ".").slice(3, defaultTimezone.offset.length));
      selectedTimezone = { name: defaultTimezone.name, offset: defaultTimezone.timeValue };
      this.selectedTimeZoneValue = defaultTimezone.offset;
      this.selectedTimezoneName.setValue(defaultTimezone.name+' '+defaultTimezone.offset);
    }

    localStorage.setItem('preferences', JSON.stringify({ language: this.selectedCountryLang.code, timezone: selectedTimezone }));
    this.commonService._preferences.next({ language: this.selectedCountryLang.code, timezone: selectedTimezone });
  }

  /**
   * apply selected theme
   */
  themeApply() {
    setTimeout(() => {
      document.documentElement.style.setProperty('--theme-color', this.isLightMode.value ? '#FFF' : '#181B20');
      document.documentElement.style.setProperty('--mat-icon-color', this.isLightMode.value ? '#5B5F61' : '#D1D6DC');
      document.documentElement.style.setProperty('--menu-hover-color', this.isLightMode.value ? '#F4F5F5' : '#232323');
      document.documentElement.style.setProperty('--icon-hover-color', this.isLightMode.value ? '#000' : '#FFF');
      document.documentElement.style.setProperty('--menu-text-color', this.isLightMode.value ? '#24292EBF' : '#D1D6DB');
      document.documentElement.style.setProperty('--selected-item-color', this.isLightMode.value ? '#D6D6D6' : '#373B3F');
      document.documentElement.style.setProperty('--header-separator-color', this.isLightMode.value ? '#EBEBEC' : '#3A3B3C');
      document.documentElement.style.setProperty('--text-color', this.isLightMode.value ? '#676A6D' : '#D1D6DB');
      document.documentElement.style.setProperty('--tooltip-color', this.isLightMode.value ? '#474B4E' : '#D1D6DB');
      document.documentElement.style.setProperty('--dashboard-bg-color', this.isLightMode.value ? '#F4F5F5' : '#101217');
      document.documentElement.style.setProperty('--menu-item-hover-color', this.isLightMode.value ? '#E9E9E9' : '#3E4146');
      document.documentElement.style.setProperty('--grid-header-bg', this.isLightMode.value ? '#FAFAFA' : '#1E2023');
      // Full component
      document.documentElement.style.setProperty('--forward-carrot-icon', this.isLightMode.value ? 'url(../../../assets/icons/forward-carrot.png)' : 'url(../../../assets/icons/forward-carrot-dark.png)');
      // Dashboard left-sidebar
      document.documentElement.style.setProperty('--icon-down-carrot', this.isLightMode.value ? 'url(../../../../assets/icons/light-down-carrot.png)' : 'url(../../../../assets/icons/dark-down-carrot.png)');
      document.documentElement.style.setProperty('--icon-up-carrot', this.isLightMode.value ? 'url(../../../../assets/icons/light-up-carrot.png)' : 'url(../../../../assets/icons/dark-up-carrot.png)');
      // TS Range Model
      document.documentElement.style.setProperty('--ts-range-fields', this.isLightMode.value ? '#24292E' : '#D1D6DC');
      document.documentElement.style.setProperty('--ts-apply-btn-bg-color', this.isLightMode.value ? '#DEDFE0' : '#32343A');
      document.documentElement.style.setProperty('--ts-calender-input-border', this.isLightMode.value ? '#A7A9AB' : '#313239');
      document.documentElement.style.setProperty('--ts-time-range-bg', this.isLightMode.value ? '#4361EE' : '#3D71D9');
      document.documentElement.style.setProperty('--ts-time-range-bg-hover', this.isLightMode.value ? '#2C5AB0' : '#5A86DE');
      document.documentElement.style.setProperty('--ts-range-input-bg', this.isLightMode.value ? '#FFF' : '#111217');
      // RT Alarms component
      document.documentElement.style.setProperty("--drawer-hed-colour", this.isLightMode.value ? "#000" : "#c1c5c8");
      document.documentElement.style.setProperty("--drawer-val-color", this.isLightMode.value ? "#24292ebf" : "#b0b4ba");
      document.documentElement.style.setProperty("--ag-row-hover-color", this.isLightMode.value ? "#e5e5e5" : "#000");
      document.documentElement.style.setProperty("--ag-date-filter-picker", this.isLightMode.value ? "light" : "dark");
      document.documentElement.style.setProperty("--font-family", 'Inter, sans-serif');
    }, 0);
  }

  //#endregion

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
    this.panelService._leftSideMenuState.next(null);
    this.panelService._isBackToDashbaord.next(false);
    this.commonService._preferences.next(null);
  }

}